import type { FilePond } from 'react-filepond'

import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { Calendar, Camera, Eye, EyeOff, Mail, Phone, User } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

import type { ChangePasswordRequest, SignUpRequest } from '@/utils/api/user-service/models'

import { FilePondUpload } from '@/components/file-pond-upload'
import SearchSelect from '@/components/search-select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/features/authentication/components/auth-context'
import { getUserInitials } from '@/lib/user-initials'
import { getGetCurentUserDetailsQueryKey, useChangePassword, useUpdateUserDetails } from '@/utils/api/user-service'

interface ProfileFormValues {
  firstName: string
  lastName: string
  otherName?: string
  email?: string
  phoneNumber?: string
  gender?: 'MALE' | 'FEMALE'
  dateOfBirth?: string
  profilePicUrl?: string
}

const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: z.string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/\d/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
}).refine(data => data.newPassword === data.confirmPassword, {
  path: ['confirmPassword'],
  message: 'Passwords do not match',
})

type ChangePasswordFormValues = z.infer<typeof changePasswordSchema>
type ProfileSectionKey = 'account' | 'personal' | 'contact'

export function ProfilePage() {
  const { user, isLoading } = useAuth()
  const queryClient = useQueryClient()
  const updateMutation = useUpdateUserDetails()
  const changePasswordMutation = useChangePassword()
  const pondRef = useRef<FilePond>(null)
  const accountSectionRef = useRef<HTMLDivElement>(null)
  const personalSectionRef = useRef<HTMLDivElement>(null)
  const contactSectionRef = useRef<HTMLDivElement>(null)
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [activeProfileSection, setActiveProfileSection] = useState<ProfileSectionKey>('account')

  const profileSectionRefs: Record<ProfileSectionKey, typeof accountSectionRef> = {
    account: accountSectionRef,
    personal: personalSectionRef,
    contact: contactSectionRef,
  }

  const form = useForm<ProfileFormValues>({
    defaultValues: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      otherName: user?.otherName ?? '',
      email: user?.email ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      gender: user?.gender,
      dateOfBirth: user?.dateOfBirth ?? '',
      profilePicUrl: user?.profilePicUrl ?? '',
    },
    values: {
      firstName: user?.firstName ?? '',
      lastName: user?.lastName ?? '',
      otherName: user?.otherName ?? '',
      email: user?.email ?? '',
      phoneNumber: user?.phoneNumber ?? '',
      gender: user?.gender,
      dateOfBirth: user?.dateOfBirth ?? '',
      profilePicUrl: user?.profilePicUrl ?? '',
    },
  })

  const changePasswordForm = useForm<ChangePasswordFormValues>({
    resolver: zodResolver(changePasswordSchema),
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
  })

  const profilePicUrl = form.watch('profilePicUrl')
  const avatarSrc = localPreviewUrl ?? profilePicUrl

  useEffect(() => {
    return () => {
      if (localPreviewUrl) {
        URL.revokeObjectURL(localPreviewUrl)
      }
    }
  }, [localPreviewUrl])

  const genderOptions = [
    { id: 'MALE', value: 'MALE', name: 'Male' },
    { id: 'FEMALE', value: 'FEMALE', name: 'Female' },
  ]

  const handleSubmit = form.handleSubmit(async (values) => {
    if (!user?.id)
      return

    const payload: SignUpRequest = {
      id: user.id,
      firstName: values.firstName,
      lastName: values.lastName,
      otherName: values.otherName,
      email: values.email,
      phoneNumber: values.phoneNumber,
      gender: values.gender,
      dateOfBirth: values.dateOfBirth,
      profilePicUrl: values.profilePicUrl,
    }

    try {
      await updateMutation.mutateAsync({ data: payload })
      await queryClient.invalidateQueries({ queryKey: getGetCurentUserDetailsQueryKey() })
      toast.success('Profile updated', {
        description: 'Your profile has been successfully updated.',
      })
    }
    catch (error: any) {
      toast.error('Update failed', {
        description: error?.response?.data?.message || 'Failed to update profile. Please try again.',
      })
    }
  })

  const handleChangePasswordSubmit = changePasswordForm.handleSubmit(async (values) => {
    const payload: ChangePasswordRequest = {
      currentPassword: values.currentPassword,
      newPassword: values.newPassword,
    }

    try {
      const response = await changePasswordMutation.mutateAsync({ data: payload })
      changePasswordForm.reset()
      toast.success('Password updated', {
        description: response?.data?.message || 'Your password has been updated successfully.',
      })
    }
    catch (error: any) {
      toast.error('Unable to update password', {
        description: error?.response?.data?.message || 'Please verify your current password and try again.',
      })
    }
  })

  const isChangingPassword = changePasswordMutation.isPending

  if (isLoading) {
    return (
      <div className="container mx-auto max-w-5xl space-y-6 p-6">
        <Skeleton className="h-12 w-64" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="container mx-auto max-w-5xl p-6">
        <Alert variant="destructive">
          <AlertDescription>Unable to load user profile. Please try logging in again.</AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-5xl space-y-8 p-6 pb-16">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">My Profile</h1>
        <p className="text-muted-foreground">
          Manage your personal information and account settings
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList>
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Form {...form}>
            <form onSubmit={handleSubmit} className="space-y-6">
              <Tabs
                value={activeProfileSection}
                onValueChange={(value) => {
                  const nextValue = value as ProfileSectionKey
                  setActiveProfileSection(nextValue)
                  profileSectionRefs[nextValue]?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }}
                className="space-y-4"
              >
                <TabsList className="flex-wrap h-auto gap-2">
                  <TabsTrigger value="account" className="flex-none">Account</TabsTrigger>
                  <TabsTrigger value="personal" className="flex-none">Personal</TabsTrigger>
                  <TabsTrigger value="contact" className="flex-none">Contact</TabsTrigger>
                </TabsList>
              </Tabs>

              <div className="space-y-6">
                <section ref={accountSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Camera className="size-5" />
                        Profile & Account
                      </CardTitle>
                      <CardDescription>
                        Upload a profile picture and view your account details
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid gap-6 md:grid-cols-3">
                        <div className="flex flex-col items-start gap-6 md:grid-cols-1">
                          <div className="relative">
                            <Avatar className="size-32 border border-border bg-background shadow-sm">
                              <AvatarImage
                                src={avatarSrc}
                                alt={user.name}
                                className="rounded-full object-cover"
                              />
                              <AvatarFallback className="text-2xl font-semibold">
                                {getUserInitials(user || '')}
                              </AvatarFallback>
                            </Avatar>
                            <Button
                              type="button"
                              variant="secondary"
                              size="icon-sm"
                              className="absolute -bottom-1 -right-1 rounded-full border-2 border-background text-white shadow-md"
                              onClick={() => pondRef.current?.browse()}
                              aria-label="Change profile photo"
                            >
                              <Camera className="size-4" />
                            </Button>
                          </div>

                          <FormField
                            name="profilePicUrl"
                            control={form.control}
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <FilePondUpload
                                    className="sr-only"
                                    pondRef={pondRef}
                                    accept={['image/png', 'image/jpeg', 'image/jpg', 'image/webp']}
                                    maxFileSize="5MB"
                                    onAddFile={(file: File) => {
                                      const previewUrl = URL.createObjectURL(file)
                                      setLocalPreviewUrl(previewUrl)
                                    }}
                                    onUploadComplete={(_fileId: string, fileUrl: string) => {
                                      field.onChange(fileUrl)
                                      setLocalPreviewUrl(null)
                                      toast.success('Photo uploaded', {
                                        description: 'Your profile picture has been uploaded. Click Save to apply changes.',
                                      })
                                    }}
                                    onUploadError={(error: string) => {
                                      toast.error('Upload failed', {
                                        description: `Failed to upload photo: ${error}`,
                                      })
                                    }}
                                    onRemove={() => setLocalPreviewUrl(null)}
                                    label={undefined}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>

                        <div className="space-y-4 md:col-span-2">
                          <div className="grid gap-4 sm:grid-cols-2">
                            <div className="space-y-1">
                              <p className="text-sm font-medium text-muted-foreground">Username</p>
                              <p className="text-sm font-semibold">{user.username || 'N/A'}</p>
                            </div>

                            <div className="space-y-1">
                              <p className="text-sm font-medium text-muted-foreground">Account Status</p>
                              <div>
                                <Badge
                                  variant={user.status === 'ACTIVE' ? 'default' : 'secondary'}
                                  className="capitalize"
                                >
                                  {user.status?.toLowerCase() || 'N/A'}
                                </Badge>
                              </div>
                            </div>

                            {user.mainOrgainisation && (
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-muted-foreground">Organisation</p>
                                <p className="text-sm font-semibold">{user.mainOrgainisation}</p>
                              </div>
                            )}

                            {user.roles && user.roles.length > 0 && (
                              <div className="space-y-1">
                                <p className="text-sm font-medium text-muted-foreground">Roles</p>
                                <div className="flex flex-wrap gap-1">
                                  {user.roles.map(role => (
                                    <Badge key={role} variant="outline" className="text-xs">
                                      {role.replaceAll('_', ' ')}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </section>

                <section ref={personalSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <User className="size-5" />
                        Personal Information
                      </CardTitle>
                      <CardDescription>
                        Update your name and personal details
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid gap-6 sm:grid-cols-2">
                        <FormField
                          name="firstName"
                          control={form.control}
                          rules={{ required: 'First name is required' }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>First Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="John" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          name="lastName"
                          control={form.control}
                          rules={{ required: 'Last name is required' }}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Last Name *</FormLabel>
                              <FormControl>
                                <Input placeholder="Doe" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          name="otherName"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Other Name / Middle Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Optional" {...field} />
                              </FormControl>
                              <FormDescription>Optional middle or other names</FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          name="gender"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <SearchSelect
                                  label="Gender"
                                  placeholder="Select gender"
                                  value={field.value}
                                  onValueChange={field.onChange}
                                  options={genderOptions}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          name="dateOfBirth"
                          control={form.control}
                          render={({ field }) => {
                            const today = new Date()
                            const minDate = new Date(today.getFullYear() - 90, 0, 1)
                            const maxDate = new Date(today.getFullYear() - 15, 11, 31)
                            const startMonth = new Date(minDate.getFullYear(), 0, 1)
                            const endMonth = new Date(maxDate.getFullYear(), 11, 31)

                            return (
                              <FormItem className="sm:col-span-2">
                                <FormLabel className="flex items-center gap-2">
                                  <Calendar className="size-4" />
                                  Date of Birth
                                </FormLabel>
                                <FormControl>
                                  <DatePicker
                                    value={field.value}
                                    onChange={field.onChange}
                                    placeholder="Select your date of birth"
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    defaultMonth={maxDate}
                                    startMonth={startMonth}
                                    endMonth={endMonth}
                                    captionLayout="dropdown"
                                  />
                                </FormControl>
                                <FormDescription>You must be at least 15 years old</FormDescription>
                                <FormMessage />
                              </FormItem>
                            )
                          }}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </section>

                <section ref={contactSectionRef} className="scroll-mt-24">
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Mail className="size-5" />
                        Contact Information
                      </CardTitle>
                      <CardDescription>
                        Manage your email and phone number
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="grid gap-6 sm:grid-cols-2">
                        <FormField
                          name="email"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Mail className="size-4" />
                                Email Address
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="john.doe@example.com"
                                  disabled
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>
                                Email cannot be changed. Contact support if needed.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          name="phoneNumber"
                          control={form.control}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="flex items-center gap-2">
                                <Phone className="size-4" />
                                Phone Number
                              </FormLabel>
                              <FormControl>
                                <Input
                                  type="tel"
                                  placeholder="+256 700 000000"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>Include country code (e.g. +256)</FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </section>
              </div>

              <Separator />

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    form.reset()
                    setLocalPreviewUrl(null)
                  }}
                  disabled={updateMutation.isPending}
                >
                  Reset Changes
                </Button>
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Form>
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
              <CardDescription>
                Update your password to keep your account secure.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...changePasswordForm}>
                <form onSubmit={handleChangePasswordSubmit} className="space-y-4">
                  <div className="grid gap-4 sm:grid-cols-1">
                    <FormField
                      name="currentPassword"
                      control={changePasswordForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Current Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showCurrentPassword ? 'text' : 'password'}
                                autoComplete="current-password"
                                disabled={isChangingPassword}
                                className="pr-10"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-pressed={showCurrentPassword}
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowCurrentPassword(prev => !prev)}
                              >
                                {showCurrentPassword
                                  ? <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  : <Eye className="h-4 w-4 text-muted-foreground" />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      name="newPassword"
                      control={changePasswordForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>New Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showNewPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                disabled={isChangingPassword}
                                className="pr-10"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-pressed={showNewPassword}
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowNewPassword(prev => !prev)}
                              >
                                {showNewPassword
                                  ? <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  : <Eye className="h-4 w-4 text-muted-foreground" />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      name="confirmPassword"
                      control={changePasswordForm.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm New Password</FormLabel>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showConfirmPassword ? 'text' : 'password'}
                                autoComplete="new-password"
                                disabled={isChangingPassword}
                                className="pr-10"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                aria-pressed={showConfirmPassword}
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowConfirmPassword(prev => !prev)}
                              >
                                {showConfirmPassword
                                  ? <EyeOff className="h-4 w-4 text-muted-foreground" />
                                  : <Eye className="h-4 w-4 text-muted-foreground" />}
                              </Button>
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <div className="flex justify-end">
                    <Button type="submit" disabled={isChangingPassword}>
                      {isChangingPassword ? 'Updating...' : 'Update Password'}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}
